import { systemValueDescription, systemValueLabel } from './systemValueLabel'

/**
 * Story 204, P17 — the generic table (SmartMateriaUITable) translates by itself every value MIC seeded, so no screen has
 * to remember it: a cell, a read-only multi-select and the options of a dropdown. What a user added is shown as typed.
 *
 * A row is the user's when it says so: `systemDefault === false` or `userDefined === true` (fixture types). A related row
 * with a code (`materialType.name`, `toolGroup.name`…) and without either flag is Sun's reference data and goes by its
 * code; a code with no key in any language falls back to the text Sun sent, so an unknown customer value reads as it is.
 * The table's own row is only translated when it carries the factory flag (`systemDefault === true` or `userDefined === false`).
 *
 * A column opts out with `translate: false`; a column with its own `render` keeps it.
 */

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

/** The user created it (or renamed it, where the screen allows it): never translated. */
const isUserRow = (value) => isObject(value) && (value.systemDefault === false || value.userDefined === true)

const isFactoryRow = (value) => isObject(value) && (value.systemDefault === true || value.userDefined === false)

const asSystemValue = (value) => (value.userDefined === undefined ? value : { ...value, systemDefault: !value.userDefined })

/**
 * The relations whose rows MIC seeds (the tables Sun's catalogue declares, SeededSystemValues): only a column that paints
 * one of them is translated by the table. A customer entity also has a code (a part reference, a resource, a worker), and
 * a code that happened to match a key would replace its name; so the table never guesses, it goes by this list. A new
 * seeded relation painted by a table is added here (and declared in Sun, where SeededRowsSchemaTest asks for it).
 */
const SEEDED_RELATIONS = new Set([
  'materialType', 'materialGroup', 'materialClass', 'technology', 'technologies', 'workerTechnologies', 'toolType', 'toolGroup',
  'fixtureType', 'featureType', 'documentType', 'storageTypes', 'storageType', 'storageFunctions', 'morphology', 'shape',
  'unitType', 'toolParameterType', 'deviceGroupType', 'ioElementSetType', 'mosCapabilities', 'otherTask', 'qualityType',
  'reason', 'reasons', 'reasonType', 'status', 'statusType', 'orderStatus', 'orderStatusType', 'fabricationOrderStatus',
  'fabricationOrderStatusType', 'foStatusType', 'fabricationOrderPlanTypeDto', 'resourceType', 'standard', 'type', 'group',
  'temporalCriteria', 'costCriteria', 'unitCriteria', 'cellUseCase', 'calendar'
])

/** Whether a column field paints a seeded relation (`materialType.name`, `technologies.name`). */
export const isSeededRelationField = (field) => {
  const path = typeof field === 'string' ? field.split('.') : []
  return path.length >= 2 && SEEDED_RELATIONS.has(path[path.length - 2])
}

const ownerOf = (row, path) => path.reduce((value, key) => (value === null || value === undefined ? value : value[key]), row)

/**
 * The text of a table cell when it is a system value, or `undefined` when the cell is not one (the table then shows its
 * value as before).
 *
 * @param {function(string, Object=): string} t_
 * @param {Object} row the table row
 * @param {string} field the column field (`name`, `materialType.name`, `toolParameterType.code`…)
 * @returns {string|undefined}
 */
export const cellSystemValueText = (t_, row, field) => {
  if (!isObject(row) || typeof field !== 'string' || field === '') return undefined
  const path = field.split('.')
  const leaf = path.pop()
  const owner = path.length === 0 ? row : ownerOf(row, path)
  if (!isObject(owner) || isUserRow(owner)) return undefined
  if (path.length === 0 && !isFactoryRow(owner)) return undefined
  if (path.length > 0 && !isSeededRelationField(field)) return undefined
  return ownerText(t_, owner, leaf, path.length > 0)
}

// Only rows with a code are translated automatically: a related row with a name and no code may be a person or anything
// a user typed (`user.name`, `worker.name`), and a common word would match a key by chance. The seeded tables without a
// code column keep an explicit `render` on their column.
const hasCode = (owner) => typeof owner.code === 'string' && owner.code !== ''

const ownerText = (t_, owner, leaf, nested) => {
  if (leaf === 'name' && hasCode(owner) && typeof owner.name === 'string' && owner.name !== '') return systemValueLabel(t_, '', asSystemValue(owner))
  if (leaf === 'description' && hasCode(owner) && typeof owner.description === 'string' && owner.description !== '') return systemValueDescription(t_, '', asSystemValue(owner))
  // A related row whose only text is its code (deviceGroupType, toolParameterType, unitType…): the code is its name.
  if (leaf === 'code' && nested && !('name' in owner) && typeof owner.code === 'string' && owner.code !== '') return systemValueLabel(t_, '', owner.code)
  return undefined
}

/**
 * The texts of a multi-select cell (`technologies.name`, `storageFunctions.code`): one per element, in order, or
 * `undefined` when the field does not go through a list of rows.
 */
const multiSystemValueTexts = (t_, row, field) => {
  if (!isObject(row) || !isSeededRelationField(field)) return undefined
  const path = field.split('.')
  const leaf = path.pop()
  const list = ownerOf(row, path)
  if (!Array.isArray(list)) return undefined
  return list.map((element) => (isObject(element) && !isUserRow(element) ? ownerText(t_, element, leaf, true) : undefined) ?? (isObject(element) ? element[leaf] : element))
}

/** The text of one value of a multi-select cell, found by its raw value among the cell's elements. */
export const multiSystemValueText = (t_, row, field, value) => {
  const path = (field || '').split('.')
  const leaf = path.pop()
  const list = ownerOf(row, path)
  if (!Array.isArray(list)) return value
  const index = list.findIndex((element) => (isObject(element) ? element[leaf] : element) === value)
  const texts = index < 0 ? undefined : multiSystemValueTexts(t_, row, field)
  return texts ? texts[index] : value
}

/** Whether any loaded option is a system value: only then the column gets an automatic label filter. */
export const hasSystemValueOptions = (items, editField) => (Array.isArray(items) ? items : []).some((item) => isObject(item) && !isUserRow(item) && hasCode(item) &&
  (editField === 'name' ? typeof item.name === 'string' : editField === 'code' && !('name' in item)))

/**
 * The label of a dropdown option: `option` is the value the column edits (`item[editField]`), `items` the rows the
 * column loaded. Returns the option itself when it is not a system value.
 */
export const optionSystemValueText = (t_, option, items, editField) => {
  if (option === null || option === undefined || option === '') return option
  const item = (Array.isArray(items) ? items : []).find((candidate) => isObject(candidate) && candidate[editField] === option)
  if (!item || isUserRow(item)) return option
  return ownerText(t_, item, editField, true) ?? option
}

/**
 * The label filter of a column that edits a related row through a loaded list, so typing the translated text filters
 * (RN1.9 / P6): the option value is what Sun stores and filters by.
 */
export const systemValueFilterOptions = (t_, items, editField) => (Array.isArray(items) ? items : [])
  .filter((item) => isObject(item) && item[editField] !== undefined && item[editField] !== null)
  .map((item) => ({ value: item[editField], label: optionSystemValueText(t_, item[editField], [item], editField) }))
